import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import styled from "styled-components";

import HtmlHead from "components/atoms/HtmlHead";
import Alert from "components/atoms/alert/Alert";
import Text from "components/atoms/text/Text";

import { BodyWithBreadcrumb, Breadcrumb, BreadcrumbTitle } from "components/molecules/Breadcrumb";
import BasicInfoTab from "components/organisms/space/BasicInfoTab";
import PaymentInfoTab from "components/organisms/space/PaymentInfoTab";
import LoginUserLayout from "components/templates/LoginUserLayout";

import { useSpacePaymentHistory } from "hooks/queries/usePayment";
import { useUser } from "hooks/queries/useUser";

import Constants from "utils/constants";
import { isAdminUser } from "utils/user";

const Container = styled.div`
    width: 100%;
`;

const TabSection = styled.div`
    display: flex;
    align-items: center;
`;

const TabButton = styled.button`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 120px;
    height: 32px;
    background-color: var(--color-White);
    border: Transparent;

    &:after {
        content: "";
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 2px;
        background-color: ${(props) => (props.active ? "var(--color-Key)" : "var(--color-DisabledText)")};
    }
`;

const TabButtonText = styled(Text).attrs({
    variant: "styled-md",
})`
    color: ${(props) => (props.active ? "var(--color-Key)" : "var(--color-DisabledText)")};
`;

const TabViewSection = styled.div`
    margin: 32px 0 0 0;
    width: 100%;
    height: 100%;
    overflow: auto;
`;

const TabViewContainer = styled.form`
    ${(props) => (props.active ? "" : "display: none;")}

    position: relative;
    width: 100%;
`;

function SpacePage() {
    const { space_uuid } = useParams();

    const [tabList, setTabList] = useState([]);
    const [tabActive, setTabActive] = useState("");

    const [user, setUser] = useState({});
    const [currentSpace, setCurrentSpace] = useState({});
    const userQuery = useUser();
    useEffect(() => {
        if (userQuery.data) {
            setUser(userQuery.data);
            setCurrentSpace(userQuery.data.current_space);
        }
    }, [userQuery.data]);

    const paymentHistoryQuery = useSpacePaymentHistory(space_uuid);
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [lastPaymentHistory, setLastPaymentHistory] = useState(null);
    useEffect(() => {
        if (paymentHistoryQuery.data) {
            setPaymentHistory(paymentHistoryQuery.data);
            setLastPaymentHistory(
                paymentHistoryQuery.data.find(
                    (history) => history.status === Constants.PAYMENT_STATUS_COMPLETED && Boolean(history.ended_at),
                ) || null,
            );
        }
    }, [paymentHistoryQuery.data]);

    // 랜딩 네비게이션
    const location = useLocation();
    useEffect(() => {
        const baseTabs = ["basicInfo", "payment"];
        setTabList(baseTabs);

        // URL에 명시적으로 전달되지 않으면 basicInfo 로 설정
        let nextTab = new URLSearchParams(location.search).get("tab") || "basicInfo";
        setTabActive(nextTab);
    }, [location.search]);

    return (
        <LoginUserLayout>
            <HtmlHead title={"스페이스 관리"} />
            <Breadcrumb>
                <BreadcrumbTitle>스페이스 관리</BreadcrumbTitle>
            </Breadcrumb>
            <BodyWithBreadcrumb>
                <Container>
                    <TabSection>
                        {tabList.map((tab) => (
                            <TabButton
                                key={tab}
                                onClick={() => {
                                    if (tab === "payment" && !isAdminUser(user)) {
                                        Alert("info", "알림", "결제 정보는 그룹 관리자만 확인할 수 있습니다.");
                                        return;
                                    }
                                    setTabActive(tab);
                                }}
                                active={tabActive === tab}
                            >
                                <TabButtonText active={tabActive === tab}>
                                    {tab === "payment" ? "결제정보" : tab === "basicInfo" ? "기본정보" : ""}
                                </TabButtonText>
                            </TabButton>
                        ))}
                    </TabSection>
                    <TabViewSection>
                        {/* 기본정보 */}
                        <TabViewContainer active={tabActive === "basicInfo"}>
                            <BasicInfoTab key={space_uuid} lastPaymentHistory={lastPaymentHistory} />
                        </TabViewContainer>

                        {/* 결제정보 */}
                        <TabViewContainer active={tabActive === "payment"}>
                            <PaymentInfoTab
                                currentSpace={currentSpace}
                                paymentHistory={paymentHistory}
                                lastPaymentHistory={lastPaymentHistory}
                            />
                        </TabViewContainer>
                    </TabViewSection>
                </Container>
            </BodyWithBreadcrumb>
        </LoginUserLayout>
    );
}

export default SpacePage;
